import type {
  GenerationRunInput,
  InvoiceRunItemsQuery,
  InvoiceRunListQuery,
  InvoiceRunStatus,
  InvoiceSkipReason,
} from '@clockwise/clients/invoices';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { invoicesApi } from './api';
import { queryKeys } from './query-keys';

/** A run in one of these is finished and immutable. Polling one is pure waste,
 *  and — more to the point — a toast that keeps spinning on a terminal run
 *  tells the user something is still happening when nothing is. */
const TERMINAL: ReadonlySet<InvoiceRunStatus> = new Set<InvoiceRunStatus>([
  'completed',
  'completed_with_errors',
  'abandoned',
]);

export function isTerminalRunStatus(status: InvoiceRunStatus | undefined): boolean {
  return status !== undefined && TERMINAL.has(status);
}

/** Foreground poll while a run is live. Fast enough that a 30-second run feels
 *  responsive, slow enough not to hammer a route that reads one row. */
const FG_POLL_MS = 2_000;
/** Backgrounded tab. The run continues server-side regardless — this only
 *  decides how stale the number is when the user looks back. */
const BG_POLL_MS = 15_000;

/**
 * Poll one run's counts while it is live, and stop dead once it is not.
 *
 * Mirrors the visibility-aware shape of `useOpenTimers`, which is the only
 * other polling query in the app. Unlike that one it does NOT poll in the
 * background at a slower rate forever: timers poll to keep the session cookie
 * sliding, and this has no such job — a terminal run has nothing left to say.
 */
export function useRunProgress(runId: string | null) {
  return useQuery({
    queryKey: queryKeys.invoices.runProgress(runId ?? ''),
    queryFn: () => invoicesApi.runProgress(runId as string),
    enabled: runId !== null,
    refetchInterval: (query) => {
      if (isTerminalRunStatus(query.state.data?.data.status)) return false;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return BG_POLL_MS;
      }
      return FG_POLL_MS;
    },
  });
}

/**
 * What a run would do, without starting one.
 *
 * `keepPreviousData` so the dialog's numbers do not blink to a spinner on every
 * filter tick — the previous answer stays on screen, slightly stale, which
 * reads far better than a flashing count. `enabled` gates on a month being
 * chosen at all.
 */
export function useRunPreview(input: GenerationRunInput | null) {
  return useQuery({
    queryKey: queryKeys.invoices.runPreview(input ?? { invoiceMonth: '' }),
    queryFn: () => invoicesApi.previewRun(input as GenerationRunInput),
    enabled: input !== null,
    placeholderData: keepPreviousData,
    // The preview is a pure function of the input and the tenant's data. Within
    // a dialog session neither moves, so re-fetching on remount is noise.
    staleTime: 30_000,
  });
}

export function useRuns(query: InvoiceRunListQuery, enabled = true) {
  return useQuery({
    queryKey: queryKeys.invoices.runs(query),
    queryFn: () => invoicesApi.listRuns(query),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useRun(runId: string) {
  return useQuery({
    queryKey: queryKeys.invoices.run(runId),
    queryFn: () => invoicesApi.getRun(runId),
    // A live run's own detail page should track it, but slowly — the stat
    // tiles are recomputed only at finalize, so a fast poll would re-render
    // the same numbers.
    refetchInterval: (query) =>
      isTerminalRunStatus(query.state.data?.data.status) ? false : 5_000,
  });
}

export function useRunItems(runId: string, query: InvoiceRunItemsQuery) {
  return useQuery({
    queryKey: queryKeys.invoices.runItems(runId, query),
    queryFn: () => invoicesApi.listRunItems(runId, query),
    placeholderData: keepPreviousData,
  });
}

/**
 * Review flags for the invoices on screen, in one request.
 *
 * Keyed on the id set and gated on it being non-empty, so a page that has not
 * loaded yet costs nothing. `staleTime` is short but non-zero: flags change
 * only when a run or an edit rewrites them, and re-fetching on every table
 * re-render would put a request behind each sort click.
 */
export function useInvoiceFlags(invoiceIds: readonly string[]) {
  return useQuery({
    queryKey: queryKeys.invoices.flags(invoiceIds),
    queryFn: () => invoicesApi.flagsForMany(invoiceIds),
    enabled: invoiceIds.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

/** Who has no invoice for a month. `enabled` so the panel costs nothing on a
 *  list page whose filter names no month. */
export function useUnbilled(month: string | null) {
  return useQuery({
    queryKey: queryKeys.invoices.unbilled(month ?? ''),
    queryFn: () => invoicesApi.unbilled(month as string),
    enabled: month !== null,
  });
}

// ---- Copy -----------------------------------------------------------------

/**
 * Plain-English reason a customer got no invoice, and whether anyone can act.
 *
 * Exhaustive over `InvoiceSkipReason` on purpose: adding a reason to the API
 * without deciding what it says here is a type error rather than a blank cell
 * on a page whose entire job is to explain itself.
 */
export function skipReasonCopy(reason: InvoiceSkipReason): {
  label: string;
  /** Null when there is nothing a user can do — render no call to action
   *  rather than one that leads nowhere. */
  fix: string | null;
} {
  switch (reason) {
    case 'NO_RETAINER_SOW':
      return {
        label: 'No engagement with a retainer line is in force.',
        fix: 'Add an engagement covering this period.',
      };
    case 'OUTSIDE_BILLING_WINDOW':
      return {
        label: 'Their engagement is not invoiced for this period.',
        fix: 'Check the engagement’s invoicing start and end dates.',
      };
    case 'NO_PRICE_FOR_PERIOD':
      return {
        label: 'No price on their engagement covers this period.',
        fix: 'Add a price tier covering this period.',
      };
    case 'FAILED':
      return {
        label: "Couldn't be invoiced due to a system error — support has been notified.",
        fix: null,
      };
    case 'RUN_ABANDONED':
      // `fix` stays null even though pressing Generate again IS the fix: the
      // only call to action this panel offers is "Open customer", and the
      // customer is not where the problem is. The instruction lives in the
      // label instead, next to the Generate button that answers it.
      return {
        label: 'The generation run stopped before reaching this engagement. Generate again.',
        fix: null,
      };
    // The rest are not problems and are filtered out server-side. They are
    // still spelled out because the run detail page shows every item, and an
    // unlabelled row there reads as a bug.
    case 'ALREADY_GENERATED':
      return { label: 'Already invoiced by an earlier run.', fix: null };
    case 'ALREADY_INVOICED':
      return { label: 'An invoice already covers this period.', fix: null };
    case 'INVOICING_PAUSED':
      return { label: 'Invoicing is paused for this customer.', fix: null };
    case 'CADENCE_NOT_DUE':
      return { label: "Their cadence doesn't bill for this period.", fix: null };
    case 'OUT_OF_SCOPE':
      return { label: 'Outside this run’s engagement types or frequencies.', fix: null };
    case 'AWAITING_BILLING_SCHEDULE':
      // Not a data gap and not a failure — the feature that schedules one-off
      // fees does not exist yet. No fix, because there is nothing the reader
      // can do about it today.
      return {
        label: 'One-time line — will be raised from the billing schedule.',
        fix: null,
      };
    // Consolidation: the fee and the hours are billed together, by ONE engine,
    // so the other stands down. Not a gap — saying "somebody else bills this"
    // is the whole point, and a reader who sees the pair without it goes
    // looking for a missing invoice that was never owed.
    case 'CONSOLIDATED_AT_PERIOD_END':
      return {
        label: 'Billed with this customer’s other work, at the end of the period.',
        fix: null,
      };
    case 'CONSOLIDATED_WITH_RETAINER':
      return {
        label: 'Billed on this customer’s retainer invoice for the period.',
        fix: null,
      };
    // ---- Time & materials -------------------------------------------------
    case 'NO_TM_RATES':
      return {
        label: 'Their engagement sells hours but prices no service role.',
        fix: 'Add a rate for each service role to their engagement.',
      };
    case 'HOURS_NOT_APPROVED':
      // The one T&M reason that IS a problem, and the reason it is not worded
      // "no approved hours": that phrasing fitted a quiet month equally well
      // and sent people to the engagement to look for a fault that was never
      // there. Hours exist; nobody has signed them off.
      return {
        label: 'Billable hours were logged for this period but none are approved.',
        fix: 'Approve the outstanding timesheets, then generate again.',
      };
    case 'HOURS_UNATTRIBUTED':
      return {
        label: 'Approved billable hours for this period are not attributed to any engagement.',
        fix: 'Attribute the timesheets to an engagement, then generate again.',
      };
    // The rest are not problems and are filtered out server-side, for the
    // same reason as the benign block above.
    case 'NO_BILLABLE_HOURS':
      return { label: 'No billable hours were logged for this period.', fix: null };
    case 'HOURS_CLAIMED_BY_ANOTHER_SOW':
      return { label: 'Their other engagement bills this period’s hours.', fix: null };
  }
}

/** How a run's status reads in a badge, and whether it needs attention. */
export function runStatusCopy(status: InvoiceRunStatus): {
  label: string;
  tone: 'success' | 'warning' | 'neutral';
} {
  switch (status) {
    case 'queued':
      return { label: 'Queued', tone: 'neutral' };
    case 'running':
      return { label: 'Running', tone: 'neutral' };
    case 'completed':
      return { label: 'Completed', tone: 'success' };
    case 'completed_with_errors':
      return { label: 'Finished with errors', tone: 'warning' };
    case 'abandoned':
      // Deliberately not "Failed": the run stopped making progress and was
      // released so the next one could start. Its customers still appear as
      // not invoiced, which is where the actionable information lives.
      return { label: 'Abandoned', tone: 'warning' };
  }
}
